import {
  ANNOTATION_EDIT_URL,
  ANNOTATION_VIEW_URL,
  Entity,
  RELATION_OWNED_BY,
  RELATION_PART_OF,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import {
  CodeSnippet,
  Table,
  TableColumn,
  TableProps,
  WarningPanel,
} from '@backstage/core-components';
import {
  getEntityRelations,
  humanizeEntityRef,
  useEntityList,
  useStarredEntities,
} from '@backstage/plugin-catalog-react';
import Typography from '@material-ui/core/Typography';
import { withStyles } from '@material-ui/core/styles';
import { visuallyHidden } from '@mui/utils';
import Edit from '@material-ui/icons/Edit';
import OpenInNew from '@material-ui/icons/OpenInNew';
import Star from '@material-ui/icons/Star';
import Add from '@material-ui/icons/Add';
import Repeat from '@material-ui/icons/Repeat';
import StarBorder from '@material-ui/icons/StarBorder';
import { startCase } from 'lodash';
import {
  useMayContributeEntities,
  useMayReuseEntities,
} from '@internal/backstage-plugin-catalog-additional-actions';
import React, { ReactNode, useMemo, useState } from 'react';
import { columnFactories } from './columns';
import { CatalogTableColumnsFunc, CatalogTableRow } from './types';
import { PaginatedCatalogTable } from './PaginatedCatalogTable';
import {
  compileEntitiesNamespacesToCounter,
  entitiesCountersToString,
  getAllowedOrDefault,
  updateQueryParams,
} from '../CatalogGrid/CatalogGrid';
import { useNavigate } from 'react-router-dom';
import { AnalyticsURLParamUtils } from '../../../utils/analytics';

/**
 * Props for {@link CatalogTable}.
 *
 * @public
 */
export interface CatalogTableProps {
  columns?: TableColumn<CatalogTableRow>[] | CatalogTableColumnsFunc;
  actions?: TableProps<CatalogTableRow>['actions'];
  tableOptions?: TableProps<CatalogTableRow>['options'];
  emptyContent?: ReactNode;
  subtitle?: string;
}

const YellowStar = withStyles({
  root: {
    color: '#f3ba37',
  },
})(Star);

export const YellowAdd = withStyles({
  root: {
    color: '#f3ba37',
  },
})(Add);

export const YellowRepeat = withStyles({
  root: {
    color: '#f3ba37',
  },
})(Repeat);

const refCompare = (a: Entity, b: Entity) => {
  const toRef = (entity: Entity) =>
    entity.metadata.title ||
    humanizeEntityRef(entity, {
      defaultKind: 'Component',
    });

  return toRef(a).localeCompare(toRef(b));
};

const pageSizeOptions = [10, 25, 50, 100];

const defaultColumnsFunc: CatalogTableColumnsFunc = ({ filters, entities }) => {
  const showTypeColumn = filters.type === undefined;

  return [
    columnFactories.createTitleColumn({ hidden: true }),
    columnFactories.createNameColumn({ defaultKind: filters.kind?.value }),
    ...createEntitySpecificColumns(),
    columnFactories.createMetadataDescriptionColumn(),
    columnFactories.createTagsColumn(),
  ];

  function createEntitySpecificColumns(): TableColumn<CatalogTableRow>[] {
    const baseColumns = [
      columnFactories.createSystemColumn(),
      columnFactories.createOwnerColumn(),
      columnFactories.createSpecTypeColumn({ hidden: !showTypeColumn }),
      columnFactories.createSpecLifecycleColumn(),
    ];
    switch (filters.kind?.value) {
      case 'user':
        return [];
      case 'domain':
      case 'system':
        return [columnFactories.createOwnerColumn()];
      case 'group':
      case 'template':
        return [
          columnFactories.createSpecTypeColumn({ hidden: !showTypeColumn }),
        ];
      case 'location':
        return [
          columnFactories.createSpecTypeColumn({ hidden: !showTypeColumn }),
          columnFactories.createSpecTargetsColumn(),
        ];
      default:
        return entities.every(entity => entity.metadata.namespace === 'default')
          ? baseColumns
          : [...baseColumns, columnFactories.createNamespaceColumn()];
    }
  }
};

/** @public */
export const CatalogTable = (props: CatalogTableProps) => {
  const { columns = defaultColumnsFunc, tableOptions, emptyContent } = props;
  const { isStarredEntity, toggleStarredEntity } = useStarredEntities();
  const { isMayContributeEntity, toggleMayContributeEntity } =
    useMayContributeEntities();
  const { isMayReuseEntity, toggleMayReuseEntity } = useMayReuseEntities();
  const entityListContext = useEntityList();
  const { loading, error, entities, filters, pageInfo } = entityListContext;
  const enablePagination = !!pageInfo;
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(window.location.search);
  AnalyticsURLParamUtils.saveQueryParamsToAnalytics(queryParams);

  const [page] = useState(parseInt(queryParams.get('page') || '0', 10) || 0);
  const [rowsPerPage] = useState(
    getAllowedOrDefault(
      10,
      parseInt(queryParams.get('rowsPerPage') || '10', 10),
      [10, 25, 50, 100],
    ),
  );

  const tableColumns = useMemo(
    () =>
      typeof columns === 'function' ? columns(entityListContext) : columns,
    [columns, entityListContext],
  );

  if (error) {
    return (
      <div>
        <WarningPanel
          severity="error"
          title="Could not fetch catalog entities."
        >
          <CodeSnippet language="text" text={error.toString()} />
        </WarningPanel>
      </div>
    );
  }

  const onPageChange = (newPage: number, newRowsPerPage?: number) =>
    updateQueryParams(navigate, newPage, newRowsPerPage);

  const defaultActions: TableProps<CatalogTableRow>['actions'] = [
    ({ entity }) => {
      const url = entity.metadata.annotations?.[ANNOTATION_VIEW_URL];
      const title = 'View';

      return {
        icon: () => (
          <>
            <Typography style={visuallyHidden}>{title}</Typography>
            <OpenInNew fontSize="small" />
          </>
        ),
        tooltip: title,
        disabled: !url,
        onClick: () => {
          if (!url) return;
          window.open(url, '_blank');
        },
      };
    },
    ({ entity }) => {
      const url = entity.metadata.annotations?.[ANNOTATION_EDIT_URL];
      const title = 'Edit';

      return {
        icon: () => (
          <>
            <Typography style={visuallyHidden}>{title}</Typography>
            <Edit fontSize="small" />
          </>
        ),
        tooltip: title,
        disabled: !url,
        onClick: () => {
          if (!url) return;
          window.open(url, '_blank');
        },
      };
    },
    ({ entity }) => {
      const isStarred = isStarredEntity(entity);
      const title = isStarred ? 'Remove from favorites' : 'Add to favorites';

      return {
        cellStyle: { paddingLeft: '1em' },
        icon: () => (
          <>
            <Typography style={visuallyHidden}>{title}</Typography>
            {isStarred ? <YellowStar /> : <StarBorder />}
          </>
        ),
        tooltip: title,
        onClick: () => toggleStarredEntity(entity),
      };
    },
    ({ entity }) => {
      const isAddedToMayContribute = isMayContributeEntity(entity);
      const title = isAddedToMayContribute
        ? 'Remove from May Contribute List'
        : 'Add to May Contribute List';

      return {
        cellStyle: { paddingLeft: '1em' },
        icon: () => (
          <>
            <Typography style={visuallyHidden}>{title}</Typography>
            {isAddedToMayContribute ? <YellowAdd /> : <Add />}
          </>
        ),
        tooltip: title,
        onClick: () => toggleMayContributeEntity(entity),
      };
    },
    ({ entity }) => {
      const isAddedToMayReuse = isMayReuseEntity(entity);
      const title = isAddedToMayReuse
        ? 'Remove from May Reuse List'
        : 'Add to May Reuse List';

      return {
        cellStyle: { paddingLeft: '1em' },
        icon: () => (
          <>
            <Typography style={visuallyHidden}>{title}</Typography>
            {isAddedToMayReuse ? <YellowAdd /> : <Add />}
          </>
        ),
        tooltip: title,
        onClick: () => toggleMayReuseEntity(entity),
      };
    },
  ];
  const realTitle =
    filters.user?.value && filters.user?.value === 'starred'
      ? 'Interested'
      : filters.user?.value;
  const titleDisplay = startCase(realTitle ?? 'all');

  const activeEntitiesCounters: { [key: string]: number } =
    compileEntitiesNamespacesToCounter(entities);
  const subtitle: string = entitiesCountersToString(activeEntitiesCounters);

  const title = `${titleDisplay} (${activeEntitiesCounters.default || 0})`;
  const actions = props.actions || defaultActions;
  const options = {
    actionsColumnIndex: -1,
    loadingType: 'linear' as const,
    showEmptyDataSourceMessage: !loading,
    padding: 'dense' as const,
    ...tableOptions,
  };

  if (enablePagination) {
    return (
      <PaginatedCatalogTable
        columns={tableColumns}
        emptyContent={emptyContent}
        isLoading={loading}
        title={title}
        actions={actions}
        subtitle={subtitle}
        onPageChange={onPageChange}
        options={options}
        data={entities.map(toEntityRow)}
        next={pageInfo.next}
        prev={pageInfo.prev}
      />
    );
  }

  const rows = entities.sort(refCompare).map(toEntityRow);

  const showPagination = rows.length > rowsPerPage;

  return (
    <Table<CatalogTableRow>
      isLoading={loading}
      columns={tableColumns}
      options={{
        paging: showPagination,
        pageSize: rowsPerPage,
        pageSizeOptions: pageSizeOptions,
        initialPage: page,
        ...options,
      }}
      title={`${titleDisplay} (${activeEntitiesCounters['default'] || 0})`}
      onPageChange={onPageChange}
      data={rows}
      actions={actions}
      subtitle={subtitle}
      emptyContent={emptyContent}
    />
  );
};

CatalogTable.columns = columnFactories;

function toEntityRow(entity: Entity) {
  const partOfSystemRelations = getEntityRelations(entity, RELATION_PART_OF, {
    kind: 'system',
  });
  const ownedByRelations = getEntityRelations(entity, RELATION_OWNED_BY);

  return {
    entity,
    resolved: {
      // This name is here for backwards compatibility mostly; the
      // presentation of refs in the table should in general be handled with
      // EntityRefLink / EntityName components
      name: humanizeEntityRef(entity, {
        defaultKind: 'Component',
      }),
      entityRef: stringifyEntityRef(entity),
      ownedByRelationsTitle: ownedByRelations
        .map(r => humanizeEntityRef(r, { defaultKind: 'group' }))
        .join(', '),
      ownedByRelations,
      partOfSystemRelationTitle: partOfSystemRelations
        .map(r =>
          humanizeEntityRef(r, {
            defaultKind: 'system',
          }),
        )
        .join(', '),
      partOfSystemRelations,
    },
  };
}
